import { useEffect, useState } from 'react';
import './customer-feedbacks-view-style.scss';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import {
  BaseLayout,
  SkeletonLoader,
  Alert,
  Badge,
  Authorizor,
  Spinner,
} from '@/components';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Button,
} from '@/components/ui';
import {
  ALERT_TYPES,
  TOAST_TYPES,
  BADGE_TYPES,
} from '@/constants/common.constants';
import {
  SURVEY_ALERT_TYPES,
  SURVEY_ALERT_TYPE_LABELS,
} from '@/constants/survey.constants';
import { useToast } from '@/contexts/toast-context';
import { useAuth } from '@/hooks/use-auth';
import {
  getCustomerFeedbackByUuid,
  markCustomerFeedbackAsViewed,
  markCustomerFeedbackAsUnviewed,
  downloadCustomerFeedbackPdf,
} from '@/api/customer-feedback.api';
import type { CustomerFeedbackResponse } from '@/types/customer-feedback.types';
import { SurveyType } from '@/types/surveys.types';
import ROLES from '@/constants/roles.constants';
import { formatSurveyDate, formatSurveyTime } from '@/lib/utils';

export const Route = createFileRoute('/customer-feedbacks/$uuid/')({
  component: CustomerFeedbackView,
});

function CustomerFeedbackView() {
  const { uuid } = Route.useParams();
  const navigate = useNavigate();
  const { addToast } = useToast();
  const { user } = useAuth();
  const [feedback, setFeedback] = useState<CustomerFeedbackResponse | null>(
    null,
  );
  const [isLoading, setIsLoading] = useState(true);
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);

  useEffect(() => {
    const fetchFeedback = async () => {
      try {
        setIsLoading(true);
        const data = await getCustomerFeedbackByUuid(uuid);
        setFeedback(data);
      } catch (err) {
        addToast(
          err instanceof Error
            ? err.message
            : 'Failed to load customer feedback',
          { type: TOAST_TYPES.ERROR },
        );
      } finally {
        setIsLoading(false);
      }
    };

    fetchFeedback();
  }, [uuid]);

  const handleNavigateSurvey = (survey: { uuid: string; type: SurveyType }) => {
    if (survey.type === SurveyType.ROUTE_SURVEY) {
      navigate({ to: `/route-surveys/${survey.uuid}` });
    } else if (survey.type === SurveyType.INCIDENT_REPORT) {
      navigate({ to: `/incident-reports/${survey.uuid}` });
    } else if (survey.type === SurveyType.CUSTOMER_FEEDBACK) {
      navigate({ to: `/customer-feedbacks/${survey.uuid}` });
    }
  };

  const handleMarkAsViewed = async () => {
    if (!feedback || !user) return;

    try {
      await markCustomerFeedbackAsViewed(uuid);
      addToast('Customer feedback marked as reviewed successfully', {
        type: TOAST_TYPES.SUCCESS,
      });
      setFeedback({
        ...feedback,
        viewed: true,
        viewedBy: { uuid: user.uuid, name: user.name },
      });
    } catch (err) {
      addToast(
        err instanceof Error
          ? err.message
          : 'Failed to mark customer feedback as reviewed',
        { type: TOAST_TYPES.ERROR },
      );
    }
  };

  const handleMarkAsUnviewed = async () => {
    if (!feedback || !user) return;

    if (feedback.viewedBy && feedback.viewedBy.uuid !== user.uuid) {
      addToast(
        'Only the person who marked this as reviewed can mark it as not reviewed',
        { type: TOAST_TYPES.ERROR },
      );
      return;
    }

    try {
      await markCustomerFeedbackAsUnviewed(uuid);
      addToast('Customer feedback marked as not reviewed successfully', {
        type: TOAST_TYPES.SUCCESS,
      });
      setFeedback({ ...feedback, viewed: false, viewedBy: null });
    } catch (err) {
      addToast(
        err instanceof Error
          ? err.message
          : 'Failed to mark customer feedback as not reviewed',
        { type: TOAST_TYPES.ERROR },
      );
    }
  };

  const handleDownloadPdf = async () => {
    setIsDownloadingPdf(true);
    try {
      await downloadCustomerFeedbackPdf(uuid);
    } catch (err) {
      addToast(err instanceof Error ? err.message : 'Failed to download PDF', {
        type: TOAST_TYPES.ERROR,
      });
    } finally {
      setIsDownloadingPdf(false);
    }
  };

  return (
    <BaseLayout
      header={{
        headerText: 'Customer Feedback',
        headerChildren:
          feedback && (feedback.previousSurvey || feedback.nextSurvey) ? (
            <div className="button-container-right customer-feedback-nav-buttons">
              <Button
                variant="outline-secondary"
                size="small"
                onClick={() =>
                  feedback.previousSurvey &&
                  handleNavigateSurvey(feedback.previousSurvey)
                }
                disabled={!feedback.previousSurvey}
              >
                <span>←</span>
                <span className="customer-feedback-nav-text">Previous</span>
              </Button>
              <Button
                variant="outline-secondary"
                size="small"
                onClick={() =>
                  feedback.nextSurvey &&
                  handleNavigateSurvey(feedback.nextSurvey)
                }
                disabled={!feedback.nextSurvey}
              >
                <span className="customer-feedback-nav-text">Next</span>
                <span>→</span>
              </Button>
            </div>
          ) : undefined,
      }}
      breadcrumbs={{
        showBreadcrumbs: true,
        breadcrumbText: ['Customer Feedback'],
      }}
    >
      {isLoading ? (
        <SkeletonLoader count={10} height={30} />
      ) : !feedback ? null : (
        <div className="customer-feedback-view">
          {/* Overview Card */}
          <Card className="customer-feedback-view-overview-card">
            <CardContent>
              <div className="customer-feedback-view-overview">
                <Authorizor allowedRoles={[ROLES.ADMINISTRATOR]}>
                  <div className="customer-feedback-view-overview-item">
                    <div className="customer-feedback-view-overview-label">
                      Account
                    </div>
                    <div>{feedback.accountName}</div>
                  </div>
                </Authorizor>
                <div className="customer-feedback-view-overview-item">
                  <span className="customer-feedback-view-overview-label">
                    Alert Type
                  </span>
                  <Badge
                    text={SURVEY_ALERT_TYPE_LABELS[SURVEY_ALERT_TYPES.COMMENTS]}
                    type={BADGE_TYPES.WARNING}
                  />
                </div>
                <div className="customer-feedback-view-overview-item">
                  <span className="customer-feedback-view-overview-label">
                    Survey Date
                  </span>
                  <span>{formatSurveyDate(feedback.surveyDate)}</span>
                </div>
                <div className="customer-feedback-view-overview-item">
                  <span className="customer-feedback-view-overview-label">
                    Survey Time
                  </span>
                  <span>{formatSurveyTime(feedback.surveyDate)}</span>
                </div>
                <Authorizor
                  allowedRoles={[
                    ROLES.ADMINISTRATOR,
                    ROLES.EXECUTIVE,
                    ROLES.MANAGER,
                    ROLES.STAFF,
                  ]}
                >
                  <div className="customer-feedback-view-overview-item">
                    <span className="customer-feedback-view-overview-label">
                      Driver
                    </span>
                    <span>
                      <a
                        className="link"
                        href={`/organizations/${feedback.driver.uuid}`}
                        onClick={(e) => {
                          if (e.ctrlKey || e.metaKey) {
                            e.stopPropagation();
                          } else {
                            e.preventDefault();
                            e.stopPropagation();
                            navigate({
                              to: `/organizations/${feedback.driver.uuid}`,
                            });
                          }
                        }}
                      >
                        {feedback.driver.name}
                      </a>
                      {feedback.driver.status === 'deactivated' && (
                        <>
                          {' '}
                          <Badge text="Deactivated" type={BADGE_TYPES.DANGER} />
                        </>
                      )}
                    </span>
                  </div>
                  <div className="customer-feedback-view-overview-item">
                    <span className="customer-feedback-view-overview-label">
                      Manager
                    </span>
                    <span>{feedback.manager}</span>
                  </div>
                </Authorizor>
                <div className="customer-feedback-view-overview-item">
                  <span className="customer-feedback-view-overview-label">
                    Category
                  </span>
                  <span>Customer Feedback</span>
                </div>
                <div className="customer-feedback-view-overview-item">
                  <span className="customer-feedback-view-overview-label">
                    Reviewed
                  </span>
                  <span
                    className={
                      feedback.viewed
                        ? 'customer-feedback-view-overview-viewed'
                        : 'customer-feedback-view-overview-not-viewed'
                    }
                  >
                    {feedback.viewed && feedback.viewedBy
                      ? `Yes [${feedback.viewedBy.name}]`
                      : 'No'}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Customer Card */}
          <Card className="customer-feedback-view-summary-card">
            <CardHeader>
              <CardTitle>Customer</CardTitle>
            </CardHeader>
            <CardContent>
              <p>{feedback.customerName}</p>
            </CardContent>
          </Card>

          {/* Customer Feedback Card */}
          <Card className="customer-feedback-view-description-card">
            <CardHeader>
              <CardTitle>Description</CardTitle>
            </CardHeader>
            <CardContent>
              <Alert
                type={ALERT_TYPES.WARNING}
                text={feedback.customerFeedback}
                className="text-neutral-dark-1"
              />
            </CardContent>
          </Card>

          <div className="button-container-right">
            <Authorizor
              allowedRoles={[
                ROLES.ADMINISTRATOR,
                ROLES.EXECUTIVE,
                ROLES.MANAGER,
                ROLES.STAFF,
              ]}
            >
              <Button
                variant="outline-secondary"
                size="medium"
                onClick={handleDownloadPdf}
                disabled={isDownloadingPdf}
              >
                {isDownloadingPdf && <Spinner size="small" />}
                Download PDF
              </Button>
            </Authorizor>
            <Authorizor
              allowedRoles={[
                ROLES.ADMINISTRATOR,
                ROLES.EXECUTIVE,
                ROLES.MANAGER,
                ROLES.STAFF,
              ]}
            >
              {feedback.canMarkAsViewed && (
                <>
                  {!feedback.viewed ? (
                    <Button
                      variant="basic-primary"
                      size="medium"
                      onClick={handleMarkAsViewed}
                    >
                      Mark as Reviewed
                    </Button>
                  ) : (
                    <Button
                      variant="outline-primary"
                      size="medium"
                      onClick={handleMarkAsUnviewed}
                      disabled={feedback.viewedBy?.uuid !== user?.uuid}
                    >
                      Mark as Not Reviewed
                    </Button>
                  )}
                </>
              )}
            </Authorizor>
          </div>
        </div>
      )}
    </BaseLayout>
  );
}
